import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CatalogueMigration,
  StockLocation,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { formatCount } from "@shared-core/domain/formatters";
import { extractFieldErrors } from "@shared-core/data/validation";
import { Button } from "../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { Label } from "../../app/components/primitives/label";
import { selectClassName } from "../stock/StockFormField";

const inputClassName =
  "px-3 py-2 border border-border rounded bg-background text-sm w-full";

/** "Leave out" is stock codes separated by commas or spaces. */
function stockCodes(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((code) => code.trim())
    .filter(Boolean);
}

/**
 * Production's cutover of one customer's stock, all or none: the warehouse it
 * lands in, staging's clean run the person attests (the date of its stock
 * reconciliation report and the "clean since" it shows), and anything left
 * out with a reason. A refusal lists everything to put right.
 */
export function CutOverDialog({
  migration,
  onClose,
  onCutOver,
}: {
  migration: CatalogueMigration;
  onClose: () => void;
  onCutOver: (message: string) => void;
}) {
  const dataAdapter = useDataAdapter();
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [reportDate, setReportDate] = useState("");
  const [cleanSince, setCleanSince] = useState("");
  const [leaveOut, setLeaveOut] = useState("");
  const [reason, setReason] = useState("");
  const [problems, setProblems] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const name = migration.customer_name ?? "this customer";

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .listStockLocations()
      .then((result) => !cancelled && setLocations(result))
      .catch((err: unknown) => {
        console.error("stock.locations.load_failed", {
          status: err instanceof ApiError ? err.status : undefined,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  async function handleCutOver() {
    setIsSaving(true);
    setProblems([]);
    const excluded = stockCodes(leaveOut);
    try {
      const result = await dataAdapter.cutOverStockOwner(
        migration.customer_id,
        {
          warehouse_id: warehouseId,
          staging_clean_report_date: reportDate || null,
          staging_clean_since: cleanSince || null,
          excluded_product_codes: excluded,
          exclusion_reason: excluded.length > 0 ? reason || null : null,
        },
      );
      onCutOver(
        `${name}'s stock is cut over: ${formatCount(result.lines.imported)} stock items are now in One.`,
      );
    } catch (err) {
      console.error("stock_cutover.cut_over.failed", {
        status: err instanceof ApiError ? err.status : undefined,
      });
      const fieldErrors = extractFieldErrors(err).map((e) => e.message);
      setProblems(
        fieldErrors.length > 0
          ? fieldErrors
          : err instanceof ApiError &&
              (err.status === 409 || err.status === 400)
            ? [err.detail]
            : [
                "Couldn't cut over the stock. Nothing was changed. Please try again.",
              ],
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Cut over {name}'s stock?</DialogTitle>
          <DialogDescription>
            {formatCount(migration.held_stock_count)} stock items will move to
            One today, all or none. From then on, call-offs and reprints for the
            stock items will run in One.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {problems.length > 0 && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              <ul className="space-y-1">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="cutover-warehouse">Warehouse</Label>
              <select
                id="cutover-warehouse"
                className={selectClassName}
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
              >
                <option value="" disabled>
                  Choose a warehouse
                </option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cutover-report-date">Staging clean report</Label>
              <input
                id="cutover-report-date"
                type="date"
                className={inputClassName}
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cutover-clean-since">Clean since</Label>
              <input
                id="cutover-clean-since"
                type="date"
                className={inputClassName}
                value={cleanSince}
                onChange={(e) => setCleanSince(e.target.value)}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="cutover-leave-out">
                Leave out{" "}
                <span className="text-muted-foreground">(optional)</span>
              </Label>
              <input
                id="cutover-leave-out"
                className={inputClassName}
                placeholder="Stock codes, with a reason"
                value={leaveOut}
                onChange={(e) => setLeaveOut(e.target.value)}
              />
              {stockCodes(leaveOut).length > 0 && (
                <input
                  aria-label="Reason for leaving them out"
                  className={inputClassName}
                  placeholder="Why they stay on Tharstern"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            If any item cannot be brought across, nothing is cut over and you
            see why.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleCutOver()}
            disabled={isSaving || !warehouseId}
          >
            {isSaving ? "Cutting over…" : "Cut over now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
